// components/ReferenceDocumentModal.tsx
import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { Modal, Button, Table, Spinner } from "react-bootstrap";
import { FileText, ExternalLink, Eye } from "lucide-react";
import { IReferenceDocument } from "../core/_models";
import { getPrincipleReferenceDocuments } from "../core/_request";
import { useSnackbar } from "../../../../../_theme/context/SnackbarContext";
import { LogError } from "../../../../../utils/GlobalLogging";
import { isAbortedError } from "../../../../../utils/global";
import { getApiErrorMessage } from "../../../../../utils/utils";

interface ReferenceDocumentModalProps {
    isOpen: boolean;
    onClose: () => void;
    projectId: number;
    principleId: number;
    title?: string;
    projectName?: string;
}

export const ReferenceDocumentModal = React.memo(({
    isOpen,
    onClose,
    projectId,
    principleId,
    title = "Reference Documents",
    projectName = "",
}: ReferenceDocumentModalProps) => {
    const { showSnackbar } = useSnackbar();
    const [documents, setDocuments] = useState<IReferenceDocument[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const abortControllerRef = useRef<AbortController | null>(null);
    const hasFetchedRef = useRef<boolean>(false);

    // Fetch documents when modal opens
    const fetchDocuments = useCallback(async () => {
        if (!projectId || !principleId) return;

        // Cancel previous request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;

        try {
            setIsLoading(true);
            const response = await getPrincipleReferenceDocuments(
                projectId,
                principleId,
                controller.signal
            );

            if (controller.signal.aborted) return;

            if (response.status === "ok") {
                setDocuments(response.data.docs);
            } else {
                showSnackbar(
                    response.message || "Failed to fetch reference documents",
                    "danger"
                );
                setDocuments([]);
            }
        } catch (error) {
            const err = error as Error;
            if (isAbortedError(err)) {
                showSnackbar(getApiErrorMessage(error, "Failed to fetch reference documents"), "danger");
                LogError("Failed to fetch reference documents", err, "ReferenceDocumentModal");
                setDocuments([]);
            }
        } finally {
            if (!controller.signal.aborted) {
                setIsLoading(false);
            }
        }
    }, [projectId, principleId, showSnackbar]);

    // Fetch documents when modal opens - using a ref to prevent infinite loop
    useEffect(() => {
        if (isOpen && projectId && principleId) {
            // Only fetch if we haven't fetched for this principleId yet
            if (hasFetchedRef.current !== true) {
                hasFetchedRef.current = true;
                fetchDocuments();
            }
        } else {
            // Reset fetch flag when modal closes
            hasFetchedRef.current = false;
        }

        // Cleanup on unmount or close
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [isOpen, projectId, principleId]); // Remove fetchDocuments from dependencies

    // Reset documents when modal closes
    useEffect(() => {
        if (!isOpen) {
            const timer = setTimeout(() => {
                setDocuments([]);
                setIsLoading(false);
                hasFetchedRef.current = false;
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    const handleClose = useCallback(() => {
        if (onClose) {
            onClose();
        }
    }, [onClose]);

    // Memoized document count text
    const documentCountText = useMemo(() => {
        return `${documents.length} document${documents.length !== 1 ? "s" : ""} found`;
    }, [documents.length]);

    // Memoized document rows
    const documentRows = useMemo(() => {
        if (isLoading) {
            return (
                <tr>
                    <td colSpan={2} className="text-center py-4">
                        <div className="d-flex flex-column align-items-center gap-2">
                            <Spinner className="text-primary spinner-border" />
                            <p className="text-muted mb-0 fw-semibold">
                                Loading documents...
                            </p>
                        </div>
                    </td>
                </tr>
            );
        }

        if (documents.length === 0) {
            return (
                <tr>
                    <td colSpan={2} className="text-center py-4">
                        <div className="d-flex flex-column align-items-center gap-2">
                            <FileText size={32} className="text-muted opacity-25" />
                            <p className="text-muted mb-0 fw-semibold">
                                There are no reference documents available.
                            </p>
                        </div>
                    </td>
                </tr>
            );
        }

        return documents.map((doc) => (
            <tr key={doc.doc_id}>
                <td className="p-2">
                    <div className="d-flex align-items-center gap-2">
                        <div className="bg-light-danger rounded p-1 d-flex align-items-center justify-content-center" style={{ width: 28, height: 28 }}>
                            <FileText size={14} className="text-danger" />
                        </div>
                        {doc.doc_file_path ? (
                        <a
                            href={doc.doc_file_path}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="fw-semibold text-gray-900 fs-7 doc-link">
                            {doc.doc_name}
                        </a>
                        ) : (
                            <span className="fw-semibold text-gray-900 fs-7">
                                {doc.doc_name}
                            </span>
                        )}
                    </div>
                </td>
                <td className="p-2">
                    <span className="text-gray-800 fs-7">
                        {doc.doc_desc || "No description available"}
                    </span>
                </td>
            </tr>
        ));
    }, [documents, isLoading]);

    return (
        <Modal
            show={isOpen}
            onHide={handleClose}
            size="lg"
            centered
            backdrop="static"
            keyboard={true}
            className="reference-document-modal"
        >
            <Modal.Header closeButton className="border-bottom p-4">
                <div className="d-flex align-items-center gap-3">
                    <div className="bg-primary bg-opacity-10 rounded-circle p-2 d-flex align-items-center justify-content-center" style={{ width: 40, height: 40 }}>
                        <FileText size={20} className="text-primary" />
                    </div>
                    <div>
                        <Modal.Title className="fw-bold">{title}</Modal.Title>
                        <p className="text-muted fs-8 mb-0">{projectName}</p>
                    </div>
                </div>
            </Modal.Header>

            <Modal.Body className="p-4">
                {/* Documents Table */}
                <div className="table-responsive" style={{ maxHeight: "50vh", overflowY: "auto" }}>
                    <Table hover className="mb-0">
                        <thead className="bg-light">
                            <tr>
                                <th style={{ width: "20%" }} className="fw-semibold text-gray-700 p-2">
                                    Document Name
                                </th>
                                <th style={{ width: "80%" }} className="fw-semibold text-gray-700 p-2">
                                    Description
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {documentRows}
                        </tbody>
                    </Table>
                </div>
            </Modal.Body>

            <Modal.Footer className="border-top justify-content-between p-4">
                {/* Document Count */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <span className="text-muted fs-8">
                        {documentCountText}
                    </span>
                </div>
                <div className="d-flex justify-content-end gap-2 align-items-center">
                    <div className="d-flex align-items-center gap-2">
                        <FileText size={14} className="text-muted" />
                        <span className="text-muted fs-8">
                            Click On  "Document Name" to open document in new tab
                        </span>
                    </div>
                    <Button variant="secondary" size="sm" onClick={handleClose}>
                        Close
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
});

// Display name for debugging
ReferenceDocumentModal.displayName = 'ReferenceDocumentModal';

// Hook to manage modal state
export const useReferenceDocumentModal = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [principleId, setPrincipleId] = useState<number>(0);

    return {
        isOpen,
        openModal: (id?: number) => {
            if (id) {
                setPrincipleId(id);
            }
            setIsOpen(true);
        },
        closeModal: () => {
            setIsOpen(false);
            setPrincipleId(0);
        },
        principleId,
    };
};